import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { appointmentService } from '../../services/appointment.service';
import { Appointment } from '../../types';
import { Loading } from '../../components/ui/Loading';

export const AppointmentsPage = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'upcoming' | 'past'>('upcoming');

  useEffect(() => {
    loadAppointments();
  }, [view]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const data = await appointmentService.getAppointments(view);
      setAppointments(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SCHEDULED':
        return 'bg-blue-100 text-blue-800';
      case 'CONFIRMED':
        return 'bg-green-100 text-green-800';
      case 'COMPLETED':
        return 'bg-gray-100 text-gray-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Appointments</h1>
        <p className="text-gray-600">View your upcoming and past appointments</p>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="flex border-b">
          <button
            onClick={() => setView('upcoming')}
            className={`flex-1 px-6 py-3 font-medium ${
              view === 'upcoming'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setView('past')}
            className={`flex-1 px-6 py-3 font-medium ${
              view === 'past'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Past
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {appointments.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">
              No {view} appointments found
            </p>
          </div>
        ) : (
          appointments.map((appointment) => (
            <div key={appointment.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{appointment.type}</h3>
                  <p className="text-sm text-gray-500">{formatDate(appointment.scheduledAt)}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(appointment.status)}`}>
                  {appointment.status}
                </span>
              </div>
              
              {appointment.partner && (
                <div className="bg-gray-50 rounded p-4 mb-4">
                  <h4 className="font-medium mb-2">Partner Details</h4>
                  <div className="space-y-1 text-sm">
                    <p><strong>Name:</strong> {appointment.partner.name}</p>
                    <p><strong>Type:</strong> {appointment.partner.type}</p>
                    {appointment.partner.location && (
                      <p><strong>Location:</strong> {appointment.partner.location}</p>
                    )}
                    {appointment.partner.contactDetails && (
                      <p><strong>Contact:</strong> {appointment.partner.contactDetails}</p>
                    )}
                  </div>
                </div>
              )}

              {appointment.notes && (
                <div className="bg-blue-50 rounded p-3">
                  <p className="text-sm text-blue-900">
                    <strong>Notes:</strong> {appointment.notes}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
